<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true")
  canvas.confetti-canvas(ref="confettiCanvas")
  .ticket-success-page
    //- Error
    .success-container(v-if="fetchError")
      .error-icon ⚠️
      h2.error-title Something went wrong
      p.error-body {{ fetchError }}
      .actions
        el-button(type="primary" @click="goToEvents" round).action-btn Back to Home

    //- Main order success
    .success-container.success-container--wide(v-else)
      //- Hero
      .success-hero
        .success-icon
          img(src="@/assets/icons/check.svg" alt="Success").success-check
        h2.success-title Thank You for Your Purchase!
        p.success-subtitle Your receipt is on the way. Check your email for confirmation.

      //- Tickets section (poll only when tickets were purchased)
      .order-section(v-if="hasTicketsExpected")
        h3.section-title
          | Your Tickets
          span.section-count ({{ tickets.length || expectedTicketCount }})
        .ticket-polling(v-if="ticketPolling")
          .processing-spinner
            .spinner-ring
          p.ticket-polling-text Issuing your tickets…
          .ticket-progress(v-if="expectedTicketCount > 0")
            .ticket-progress-track
              .ticket-progress-fill(:style="{ width: `${ticketProgressPercent}%` }")
            p.ticket-progress-label {{ ticketsFoundSoFar }} of {{ expectedTicketCount }} tickets ready
        .ticket-timeout(v-else-if="ticketTimedOut")
          p.ticket-polling-text Your tickets will be available shortly. Check your email.
        .tickets-grid(v-else-if="tickets.length")
          .tv-ticket-card(v-for="ticket in tickets" :key="ticket.id")
            .tv-card-image(
              :class="{ 'tv-card-image--sticker': !getStickerForTicket(ticket)?.bannerUrl }"
            )
              .tv-shimmer-overlay
              img(
                v-if="getStickerForTicket(ticket)?.bannerUrl || getStickerForTicket(ticket)?.url"
                :src="getStickerForTicket(ticket)?.bannerUrl || getStickerForTicket(ticket)?.url"
                :alt="getStickerForTicket(ticket)?.title"
              ).tv-card-img
            .tv-card-body
              h3.tv-event-name {{ getStickerForTicket(ticket)?.title || 'Event Ticket' }}
              .tv-info-grid(v-if="getStickerForTicket(ticket)?.eventDate")
                .tv-info-cell
                  .tv-info-label Date
                  .tv-info-value {{ formatTicketDate(getStickerForTicket(ticket)?.eventDate) }}
                .tv-info-cell
                  .tv-info-label Time
                  .tv-info-value {{ formatTicketTime(getStickerForTicket(ticket)?.eventDate) }}
              .tv-info-row(
                v-if="getStickerForTicket(ticket)?.location && !getStickerForTicket(ticket)?.hideLocation"
              )
                .tv-info-label Location
                .tv-info-value {{ getStickerForTicket(ticket)?.location }}
              .tv-info-row
                .tv-info-label Ticket Type
                .tv-info-value
                  | {{ getTicketTierName(ticket) }}
                  span.tv-option-text(v-if="ticket.selectedOption") &nbsp;· {{ ticket.selectedOption }}
              .tv-seat-grid(v-if="ticket.section || ticket.row || ticket.seat")
                .tv-seat-cell(v-if="ticket.section")
                  .tv-seat-value {{ ticket.section }}
                  .tv-seat-label SEC
                .tv-seat-cell(v-if="ticket.row")
                  .tv-seat-value {{ ticket.row }}
                  .tv-seat-label ROW
                .tv-seat-cell(v-if="ticket.seat")
                  .tv-seat-value {{ ticket.seat }}
                  .tv-seat-label SEAT
              .tv-qr-wrap
                qrcode-vue(:value="ticketQrUrl(ticket)" :size="160" level="H" render-as="svg")
              .tv-checked-in(v-if="ticket.checkedInAt")
                img(src="@/assets/icons/check.svg" alt="").status-icon
                span Checked in {{ formatDateTime(ticket.checkedInAt) }}
              .tv-not-checked-in(v-else)
                span Not yet checked in
              .tv-code {{ ticket.code }}

      //- Non-ticket items (campaign, sweepstakes, free, donations, glow)
      .order-section(v-if="otherItems.length")
        h3.section-title Also Included in Your Order
        .order-items-list
          .order-item(v-for="(item, idx) in otherItems" :key="`${item.lineType}-${item.stickerId}-${idx}`")
            img(src="@/assets/icons/check.svg" alt="").order-item-check
            .order-item-info
              .order-item-title {{ formatOrderLineTitle(item) }}
              .order-item-detail {{ formatOrderLineDetail(item) }}

      //- Anonymous upsell
      .anon-upsell(v-if="!isAuthenticated")
        h3.upsell-title Save your purchases
        p.upsell-body Create an account to access your tickets and stickers anytime.
        el-button(type="primary" @click="goToSignUp" round).upsell-btn Create Account

      .actions
        el-button(
          v-if="hasTicketsExpected"
          type="primary"
          @click="goToTicketOrders"
          round
        ).action-btn View My Tickets
        el-button(
          v-if="otherItems.length || !hasTicketsExpected"
          :type="hasTicketsExpected ? 'default' : 'primary'"
          @click="goToPurchaseHistory"
          round
        ).action-btn Purchase History
        el-button(type="default" @click="goToEvents" round).action-btn Back to Home
</template>

<script lang="ts">
import {
  computed, defineComponent, nextTick, onMounted, onUnmounted, ref, watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import QrcodeVue from 'qrcode.vue';
import dayjs from 'dayjs';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import { makeRequest } from '@/graphs/index';
import { findMyTickets, findTicketAvailability, findSticker } from '@/graphs/gql';
import { useConfetti } from '@/composables/useConfetti';
import {
  buildVariantTitleMap,
  clearTicketCheckoutContext,
  formatOrderLineDetail,
  formatOrderLineTitle,
  getNewCheckoutTickets,
  getNonTicketSummaries,
  hasAllCheckoutTickets,
  loadGuestCheckoutContact,
  loadTicketCheckoutContext,
  saveGuestCheckoutContact,
  resolveTicketTierName,
  type CartLineSummary,
} from '@/utils/ticketDisplay';
import {
  captureEmbedOrganizationContext,
  navigateToEmbedOrganizationHome,
  requestEmbedHeightSync,
  scrollEmbedToTopAfterHeightSync,
} from '@/utils/embeddedUtils';
import { ticketError, ticketLog, ticketWarn } from '@/utils/ticketLogger';

interface IssuedTicket {
  id: string;
  code: string;
  tierName: string;
  selectedOption?: string;
  stickerId: string;
  variantId: string;
  section?: string;
  row?: string;
  seat?: string;
  checkedInAt?: string;
  createdAt: string;
}

interface StickerInfo {
  id: string;
  title: string;
  bannerUrl?: string;
  url?: string;
  eventDate?: string;
  location?: string;
  hideLocation?: boolean;
}

const MAX_POLLS = 15;
const POLL_INTERVAL_MS = 2000;

export default defineComponent({
  name: 'TicketSuccess',
  components: { WebAppLayout, QrcodeVue },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const { initConfetti, stopConfetti, triggerConfettiBurst } = useConfetti();

    const me = computed(() => store.getters['users/me']);
    const isAuthenticated = computed(() => !!me.value);

    const rawOid = route.query.orderId;
    const rawOidStr = Array.isArray(rawOid) ? (rawOid[0] ?? '') : (typeof rawOid === 'string' ? rawOid : '');
    const orderId = ref(rawOidStr === '[object Object]' ? '' : rawOidStr);
    const isFreeOrder = route.query.free === 'true';
    const rawTicketCount = route.query.ticketCount;
    const fallbackExpectedCount = Number(
      Array.isArray(rawTicketCount) ? rawTicketCount[0] : rawTicketCount,
    ) || 0;
    const routeStickerId = (route.query.stickerId as string) || '';

    const confettiCanvas = ref<HTMLCanvasElement | null>(null);
    const tickets = ref<IssuedTicket[]>([]);
    const stickersById = ref<Record<string, StickerInfo>>({});
    const variantTitleMap = ref<Record<string, string>>({});
    const ticketPolling = ref(false);
    const ticketTimedOut = ref(false);
    const fetchError = ref<string | null>(null);
    const primaryStickerId = ref<string>(routeStickerId);
    const stickerIdsToPoll = ref<string[]>(routeStickerId ? [routeStickerId] : []);
    const otherItems = ref<CartLineSummary[]>([]);
    const expectedTicketCount = ref(0);
    const ticketsFoundSoFar = ref(0);

    let pollCount = 0;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    const hasTicketsExpected = computed(() => expectedTicketCount.value > 0);

    const ticketProgressPercent = computed(() => {
      if (!expectedTicketCount.value) return 0;
      return Math.min(
        100,
        Math.round((ticketsFoundSoFar.value / expectedTicketCount.value) * 100),
      );
    });

    function getCheckoutContext() {
      const context = loadTicketCheckoutContext();
      if (!context) return null;
      if (context.orderId && orderId.value && context.orderId !== orderId.value) {
        ticketWarn('Checkout context orderId mismatch — ignoring context', {
          contextOrderId: context.orderId,
          routeOrderId: orderId.value,
        });
        return null;
      }
      return context;
    }

    function countNewTickets(
      allTickets: IssuedTicket[],
      checkoutContext: ReturnType<typeof getCheckoutContext>,
    ): number {
      if (checkoutContext) {
        return allTickets.filter(
          (ticket) => !checkoutContext.existingTicketIds.includes(ticket.id),
        ).length;
      }
      return allTickets.length;
    }

    function resolveStickerIds(): string[] {
      const context = getCheckoutContext();
      if (context?.stickerIds?.length) return context.stickerIds;

      const cartLines = store.getters['cart/cartLines'] || [];
      const fromCart = [...new Set(
        cartLines
          .filter((line: { lineType: string; stickerId: string }) => line.lineType === 'Ticket')
          .map((line: { stickerId: string }) => line.stickerId),
      )];

      if (fromCart.length) return fromCart;
      if (routeStickerId) return [routeStickerId];
      return [];
    }

    function finishCheckout() {
      store.dispatch('cart/clearCart');
      clearTicketCheckoutContext();
    }

    function startConfetti() {
      nextTick(() => {
        if (confettiCanvas.value) {
          initConfetti(confettiCanvas.value);
          triggerConfettiBurst(confettiCanvas.value);
        }
      });
    }

    async function loadVariantTitles(stickerIds: string[]) {
      const merged: Record<string, string> = {};
      for (const stickerId of stickerIds) {
        try {
          const response = await makeRequest(
            'query',
            findTicketAvailability,
            { stickerId },
            me.value?.token ? { token: me.value.token } : null,
          );
          const tiers = response?.data?.findTicketAvailability || [];
          Object.assign(merged, buildVariantTitleMap(tiers));
        } catch (err) {
          ticketError('Failed to load variant titles', { stickerId, err });
        }
      }
      variantTitleMap.value = merged;
    }

    async function loadStickers(stickerIds: string[]) {
      for (const stickerId of stickerIds) {
        if (stickersById.value[stickerId]) continue;
        try {
          const response = await makeRequest(
            'query',
            findSticker,
            { id: stickerId },
            me.value?.token ? { token: me.value.token } : null,
          );
          const sticker = response?.data?.findSticker;
          if (sticker) {
            stickersById.value = {
              ...stickersById.value,
              [stickerId]: sticker,
            };
          }
        } catch (err) {
          ticketError('Failed to load sticker', { stickerId, err });
        }
      }
    }

    function getTicketTierName(ticket: IssuedTicket) {
      return resolveTicketTierName(ticket, variantTitleMap.value);
    }

    function getStickerForTicket(ticket: IssuedTicket): StickerInfo | undefined {
      return stickersById.value[ticket.stickerId];
    }

    async function fetchAllTickets(stickerIds: string[]): Promise<IssuedTicket[]> {
      const batches = await Promise.all(stickerIds.map(async (stickerId) => {
        try {
          const response = await makeRequest(
            'query',
            findMyTickets,
            { stickerId },
            { token: me.value.token },
          );
          return (response?.data?.findMyTickets || []) as IssuedTicket[];
        } catch (err) {
          ticketError('Failed to fetch tickets for sticker', { stickerId, err });
          return [];
        }
      }));

      return batches.flat();
    }

    async function pollTickets() {
      if (!me.value?.token) {
        ticketWarn('No auth token — cannot poll tickets');
        ticketPolling.value = false;
        ticketTimedOut.value = true;
        finishCheckout();
        return;
      }

      if (!stickerIdsToPoll.value.length) {
        stickerIdsToPoll.value = resolveStickerIds();
      }

      if (!stickerIdsToPoll.value.length) {
        ticketError('No sticker IDs available for ticket polling');
        ticketPolling.value = false;
        ticketTimedOut.value = true;
        finishCheckout();
        return;
      }

      if (!primaryStickerId.value) {
        primaryStickerId.value = stickerIdsToPoll.value[0];
      }

      try {
        await Promise.all([
          loadVariantTitles(stickerIdsToPoll.value),
          loadStickers(stickerIdsToPoll.value),
        ]);

        const data = await fetchAllTickets(stickerIdsToPoll.value);
        const checkoutContext = getCheckoutContext();
        const expectedCount = checkoutContext?.expectedCount || fallbackExpectedCount;

        ticketsFoundSoFar.value = countNewTickets(data, checkoutContext);
        ticketLog('Ticket poll progress', {
          pollCount,
          ticketsFoundSoFar: ticketsFoundSoFar.value,
          expectedCount,
        });

        const ready = hasAllCheckoutTickets(
          data,
          checkoutContext,
          fallbackExpectedCount,
        );

        if (ready) {
          tickets.value = getNewCheckoutTickets(data, checkoutContext);
          ticketsFoundSoFar.value = tickets.value.length;
          ticketPolling.value = false;
          finishCheckout();
          return;
        }

        pollCount += 1;
        const maxPolls = isFreeOrder ? 5 : MAX_POLLS;
        ticketLog('Tickets not ready yet — scheduling next poll', { pollCount, maxPolls });

        if (pollCount >= maxPolls) {
          ticketWarn('Ticket polling timed out', { pollCount, expectedCount });
          ticketPolling.value = false;
          ticketTimedOut.value = true;
          finishCheckout();
          return;
        }

        pollTimer = setTimeout(pollTickets, POLL_INTERVAL_MS);
      } catch (err: any) {
        ticketError('Poll error', err);
        pollCount += 1;
        if (pollCount >= MAX_POLLS) {
          ticketPolling.value = false;
          ticketTimedOut.value = true;
          finishCheckout();
          return;
        }
        pollTimer = setTimeout(pollTickets, POLL_INTERVAL_MS);
      }
    }

    function goToEvents() {
      const checkoutContext = getCheckoutContext();
      const embedContext = captureEmbedOrganizationContext(route);

      const navigated = navigateToEmbedOrganizationHome(route, {
        organizationId: checkoutContext?.embedOrganizationId
          || embedContext.organizationId
          || undefined,
        embedSource: checkoutContext?.embedSource
          || embedContext.embedSource
          || undefined,
      }, { router });

      if (navigated) {
        return;
      }

      router.push('/web/dashboard');
    }

    function goToTicketOrders() {
      router.push('/web/ticket-orders');
    }

    function goToPurchaseHistory() {
      router.push('/web/stickers/purchased');
    }

    function goToSignUp() {
      const context = getCheckoutContext();
      const stored = loadGuestCheckoutContact();
      const email = context?.email || stored.email;
      const phone = context?.phone || stored.phone;
      const redirect = route.fullPath;

      if (email) {
        saveGuestCheckoutContact(email, phone);
        router.push({
          path: '/auth/web/complete-profile',
          query: { email, redirect },
        });
        return;
      }

      router.push({
        path: '/auth/web/signup',
        query: { redirect },
      });
    }

    function ticketQrUrl(ticket: IssuedTicket) {
      return `${window.location.origin}/web/ticket/${ticket.stickerId}/detail?ticketId=${ticket.id}`;
    }

    function formatTicketDate(date?: string) {
      if (!date) return '—';
      return dayjs(date).format('MMMM Do, YYYY');
    }

    function formatTicketTime(date?: string) {
      if (!date) return '—';
      return dayjs(date).format('hh:mm A');
    }

    function formatDateTime(date: string) {
      return dayjs(date).format('MMM D, YYYY h:mm A');
    }

    watch(
      () => [
        ticketPolling.value,
        tickets.value.length,
        ticketTimedOut.value,
        otherItems.value.length,
        fetchError.value,
      ],
      () => {
        nextTick(() => {
          requestEmbedHeightSync();
          scrollEmbedToTopAfterHeightSync('ticket-success-content');
        });
      },
    );

    onMounted(() => {
      captureEmbedOrganizationContext(route);

      const context = getCheckoutContext();
      expectedTicketCount.value = context?.expectedCount || fallbackExpectedCount;
      otherItems.value = getNonTicketSummaries(context?.cartLineSummary || []);
      stickerIdsToPoll.value = resolveStickerIds();

      ticketLog('TicketSuccess mounted', {
        orderId: orderId.value,
        expectedTicketCount: expectedTicketCount.value,
        otherItemCount: otherItems.value.length,
        stickerIdsToPoll: stickerIdsToPoll.value,
        checkoutContext: context,
      });

      startConfetti();

      if (hasTicketsExpected.value) {
        ticketPolling.value = true;
        pollTickets();
      } else {
        finishCheckout();
      }

      nextTick(() => {
        requestEmbedHeightSync();
        scrollEmbedToTopAfterHeightSync('ticket-success-loaded');
      });
    });

    onUnmounted(() => {
      if (pollTimer) clearTimeout(pollTimer);
      stopConfetti();
    });

    return {
      confettiCanvas,
      orderId,
      tickets,
      ticketPolling,
      ticketTimedOut,
      fetchError,
      isAuthenticated,
      otherItems,
      hasTicketsExpected,
      expectedTicketCount,
      ticketsFoundSoFar,
      ticketProgressPercent,
      goToEvents,
      goToSignUp,
      goToTicketOrders,
      goToPurchaseHistory,
      ticketQrUrl,
      getTicketTierName,
      getStickerForTicket,
      formatTicketDate,
      formatTicketTime,
      formatDateTime,
      formatOrderLineTitle,
      formatOrderLineDetail,
    };
  },
});
</script>

<style lang="scss" scoped>
.confetti-canvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 9999;
}

.ticket-success-page {
  min-height: 100vh;
  background: #f5f5f7;
  padding: 1.5rem 1.5rem 5rem;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 2rem 2.5rem 5rem;
  }

  @media (min-width: 1024px) {
    padding: 2.5rem 4rem 5rem;
  }
}

.success-container {
  max-width: 480px;
  margin: 0 auto;
  text-align: center;
  width: 100%;

  &--wide {
    max-width: 960px;
  }
}

.success-hero {
  background: #fff;
  border-radius: 16px;
  padding: 2rem 1.5rem;
  margin-bottom: 0.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;

  @media (min-width: 768px) {
    padding: 2.5rem 2rem;
  }
}

.processing-spinner {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.spinner-ring {
  width: 40px;
  height: 40px;
  border: 4px solid #e0e0e0;
  border-top-color: #5865f2;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.processing-title,
.success-title,
.error-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem;
  line-height: 1.25;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
}

.processing-subtitle,
.success-subtitle,
.error-body {
  font-size: 0.95rem;
  color: #666;
  margin: 0;
  line-height: 1.5;
  max-width: 28rem;
  margin-left: auto;
  margin-right: auto;
}

.processing-debug {
  display: none;
}

.success-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.success-check {
  width: 56px;
  height: 56px;

  @media (min-width: 768px) {
    width: 64px;
    height: 64px;
  }
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.order-section {
  margin-top: 1.5rem;
  text-align: left;
  background: #fff;
  border-radius: 16px;
  padding: 1.25rem 1.25rem 1.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;

  @media (min-width: 768px) {
    padding: 1.5rem 1.75rem 1.75rem;
  }
}

.section-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.section-count {
  font-size: 0.9rem;
  font-weight: 500;
  color: #6b7280;
}

.ticket-polling,
.ticket-timeout {
  background: #f9fafb;
  border-radius: 12px;
  padding: 1.5rem 1.25rem;
  text-align: center;
  border: 1px solid #e5e7eb;
}

.ticket-polling-text {
  font-size: 0.95rem;
  color: #666;
  margin: 0.75rem 0 0;
}

.ticket-progress {
  margin-top: 1.25rem;
  max-width: 20rem;
  margin-left: auto;
  margin-right: auto;
}

.ticket-progress-track {
  height: 6px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.ticket-progress-fill {
  height: 100%;
  background: #5865f2;
  border-radius: 999px;
  transition: width 0.4s ease;
  min-width: 4px;
}

.ticket-progress-label {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0.5rem 0 0;
  font-weight: 500;
}

.order-items-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.order-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background: #f9fafb;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  border: 1px solid #e5e7eb;
}

.order-item-check {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.order-item-info {
  flex: 1;
  min-width: 0;
}

.order-item-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.2rem;
}

.order-item-detail {
  font-size: 0.85rem;
  color: #6b7280;
}

.tickets-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.5rem;
  }
}

.tv-ticket-card {
  background: #f9fafb;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  min-width: 0;
}

.tv-card-image {
  position: relative;
  height: 180px;
  overflow: hidden;
  background: #e5e7eb;

  .tv-shimmer-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      105deg,
      transparent 30%,
      rgba(255, 255, 255, 0.35) 50%,
      transparent 70%
    );
    background-size: 200% 100%;
    animation: tvShimmer 3s infinite linear;
    z-index: 1;
    pointer-events: none;
  }

  .tv-card-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    position: relative;
    z-index: 2;
  }

  &.tv-card-image--sticker {
    background: linear-gradient(180deg, #f0fdfa 0%, #f9fafb 100%);

    .tv-card-img {
      object-fit: contain;
      padding: 1rem;
      box-sizing: border-box;
    }
  }
}

@keyframes tvShimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.tv-card-body {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.tv-event-name {
  font-size: 1.2rem;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.2;
}

.tv-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.tv-info-cell,
.tv-info-row {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.tv-info-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: 600;
  color: #9ca3af;
}

.tv-info-value {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1a1a1a;
}

.tv-option-text {
  font-size: 0.85rem;
  font-weight: 500;
  color: #6b7280;
}

.tv-seat-grid {
  display: flex;
  justify-content: center;
  gap: 2rem;
  padding: 0.75rem 0;
  border-top: 1px solid #f3f4f6;
  border-bottom: 1px solid #f3f4f6;
}

.tv-seat-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.tv-seat-value {
  font-size: 1.5rem;
  font-weight: 900;
  color: #0d9488;
  line-height: 1;
}

.tv-seat-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #9ca3af;
  text-transform: uppercase;
}

.tv-qr-wrap {
  display: flex;
  justify-content: center;
  padding: 0.25rem 0;
}

.tv-checked-in {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  color: #0d9488;
  font-size: 0.8rem;
  font-weight: 600;

  .status-icon {
    width: 1rem;
    height: 1rem;
  }
}

.tv-not-checked-in {
  text-align: center;
  font-size: 0.8rem;
  color: #9ca3af;
}

.tv-code {
  text-align: center;
  font-size: 0.7rem;
  font-family: monospace;
  color: #d1d5db;
  word-break: break-all;
}

.anon-upsell {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  text-align: center;

  .upsell-title {
    font-size: 1.1rem;
    font-weight: 700;
    margin: 0 0 0.5rem;
    color: #1a1a1a;
  }

  .upsell-body {
    font-size: 0.9rem;
    color: #555;
    margin: 0 0 1rem;
  }

  .upsell-btn {
    width: 100%;
    max-width: 260px;
  }
}

.actions {
  margin-top: 1.5rem;
  padding-bottom: 1rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.625rem;

  @media (min-width: 640px) {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
  }
}

.action-btn {
  width: 100%;
  font-weight: 600;

  @media (min-width: 640px) {
    width: auto;
    min-width: 0;
    flex: 0 1 auto;
    max-width: 240px;
  }
}
</style>
